import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar, Filter, Download, RefreshCw, User, Clock, Target, FileText } from 'lucide-react';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';

const AdminActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  
  // Pagination
  const [limit] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchActionTypes();
    fetchStats();
    fetchLogs();
  }, []);

  const fetchActionTypes = async () => {
    try {
      const response = await adminAPI.getActivityLogActionTypes();
      setActionTypes(response.data.action_types || []);
    } catch (error) {
      console.error('Failed to fetch action types:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getActivityLogStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * limit;
      const response = await adminAPI.getActivityLogs({
        start_date: startDate,
        end_date: endDate,
        role: roleFilter,
        action_type: actionTypeFilter,
        limit,
        skip
      });
      
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchLogs();
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setRoleFilter('');
    setActionTypeFilter('');
    setCurrentPage(1);
    setTimeout(fetchLogs, 100);
  };

  const handleExportCSV = () => {
    if (logs.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Date & Time', 'Performed By', 'Role', 'Action Type', 'Description', 'Target'];
    const csvData = logs.map(log => [
      new Date(log.created_at).toLocaleString('en-US', { timeZone: 'Asia/Dhaka' }),
      log.performed_by_name,
      log.performed_by_role.toUpperCase(),
      log.action_type,
      log.action_description,
      log.target_name || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Activity log exported successfully');
  };

  const getActionBadgeColor = (actionType) => {
    const colors = {
      'STUDENT_ADD': 'bg-green-100 text-green-800',
      'STUDENT_DELETE': 'bg-red-100 text-red-800',
      'STUDENT_UPDATE': 'bg-blue-100 text-blue-800',
      'CREDIT_ADD': 'bg-emerald-100 text-emerald-800',
      'CREDIT_DEDUCT': 'bg-orange-100 text-orange-800',
      'PASSWORD_CHANGE': 'bg-purple-100 text-purple-800',
      'BOOKING_CREATE': 'bg-teal-100 text-teal-800',
      'BOOKING_CANCEL': 'bg-pink-100 text-pink-800',
      'MODERATOR_ADD': 'bg-indigo-100 text-indigo-800',
      'MODERATOR_UPDATE': 'bg-cyan-100 text-cyan-800',
      'PERMISSION_CHANGE': 'bg-yellow-100 text-yellow-800'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.total_logs}</div>
                <div className="text-sm text-gray-600 mt-1">Total Activities</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{stats.admin_actions}</div>
                <div className="text-sm text-gray-600 mt-1">Admin Actions</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{stats.moderator_actions}</div>
                <div className="text-sm text-gray-600 mt-1">Moderator Actions</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Start Date */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Role Filter */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Role</label>
              <Select value={roleFilter || 'all'} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Action Type Filter */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Action Type</label>
              <Select value={actionTypeFilter || 'all'} onValueChange={(v) => setActionTypeFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex gap-2 mt-4">
            <Button onClick={handleApplyFilters} className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Apply Filters
            </Button>
            <Button variant="outline" onClick={handleResetFilters} className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Reset
            </Button>
            <Button variant="outline" onClick={handleExportCSV} className="flex items-center gap-2 ml-auto">
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Activity Log ({logs.length} records)
            </span>
            <Button size="sm" variant="ghost" onClick={fetchLogs}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No activity logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performed By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-900">
                            {new Date(log.created_at).toLocaleString('en-US', {
                              timeZone: 'Asia/Dhaka',
                              month: 'short',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">{log.performed_by_name}</div>
                            <Badge className={log.performed_by_role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                              {log.performed_by_role.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge className={getActionBadgeColor(log.action_type)}>
                          {log.action_type.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {log.action_description}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.target_name ? (
                          <div className="flex items-center gap-1">
                            <Target className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-900">{log.target_name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Page {currentPage}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                    setTimeout(fetchLogs, 100);
                  }}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCurrentPage(prev => prev + 1);
                    setTimeout(fetchLogs, 100);
                  }}
                  disabled={logs.length < limit}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityLog;
